import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import ReportDTO from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import GenericCrudServiceV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";
import Assignation from "../models/Assignation";
import AssignationProjectionReport from "../models/AssignationProjectionReport";
import AssignationTransfer from "../models/AssignationTransfer";
import AssignationTransferBuilder from "../models/AssignationTransferBuilder";
import AssignationTransferForm from "../models/AssignationTransferForm";
import OverStaffAssignation from "../models/OverStaffReport/OverStaffAssignation";
import Position from "../models/Position";
import ProjectData from "../models/ProjectData";
import Projection from "../models/Projection";
import ProjectionAssignation from "../models/ProjectionAssignation";
import ProjectionPeople from "../models/ProjectionPeople";

class AssignationService extends GenericCrudServiceV2<Assignation> {
  readonly path: string;

  constructor(path: string) {
    super(path);
    this.path = path;
  }

  findByProjectExternalId(
    page: number,
    pageSize: number,
    projectExternalId: string,
    statusFilter: string,
  ): Promise<ApiResponseV2<PagedResult<Assignation>>> {
    let query = `${this.basePath}/find-filtered?page=${page}&pageSize=${pageSize}&projectExternalId=${projectExternalId}&statusFilter=${statusFilter}`;
    return HttpClientV2.get(query);
  }

  parseAssignationTransferForm(formData: AssignationTransferForm): AssignationTransfer {
    const position: Position = {
      externalId: formData.positionExternalId ? formData.positionExternalId : "",
      externalRoleName: formData.externalRoleName,
      internalRoleName: formData.internalRoleName,
      externalHours: formData.externalHours,
      internalHours: formData.internalHours,
      shadow: formData.shadow,
      bonus: formData.bonus,
      billingPolicy: formData.billingPolicy,
      shortEndDate: formData.shortEndDate,
      shortStartDate: formData.shortStartDate,
      roleKey: formData.roleKey,
      onDemand: formData.onDemand,
      monthlyMinimumHours: formData.monthlyMinimumHours,
      memberExternalId: formData.userExternalId,
      projectExternalId: formData.projectExternalId,
    };

    const transfer: AssignationTransfer = {
      externalId: formData.externalId,
      userExternalId: formData.userExternalId,
      oldPositionExternalId: formData.oldPositionExternalId,
      shortStartDate: formData.shortStartDate,
      shortEndDate: formData.shortEndDate,
      position: position,
      userFullName: "",
    };

    return transfer;
  }

  findAssignationsNamesByUser(userExternalId?: string, date: Date | null = null): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    let query = `${this.basePath}/assignation-names`;
    query += userExternalId ? `?userExternalId=${userExternalId}` : "";
    query +=
      date && userExternalId
        ? `&month=${date.getMonth() + 1}&year=${date.getFullYear()}`
        : date
          ? `?month=${date.getMonth() + 1}&year=${date.getFullYear()}`
          : "";
    return HttpClientV2.get(query);
  }

  getProjectionData(): Projection {
    let projection = new Projection();
    this.getOverStaffProjection().then((proj) => {
      let response = proj.getContentOrThrowError();

      let peopleArray: ProjectionPeople[] = [];
      response.people.forEach((p) => {
        let assignationArray: ProjectionAssignation[] = [];
        p.assignations.forEach((a) => {
          assignationArray.push(
            new ProjectionAssignation(
              a.projectName,
              a.firstName,
              a.lastName,
              a.userExternalId,
              a.roleType,
              a.externalHours,
              a.bonusHours,
              a.shadow,
              a.externalId,
            ),
          );
        });
        peopleArray.push(new ProjectionPeople(p.firstName, p.lastName, p.userExternalId, p.email, assignationArray, p.availableHours));
      });

      let projectsArray: ProjectData[] = [];
      response.projects.forEach((p) => {
        let assignationArray: ProjectionAssignation[] = [];
        p.assignations.forEach((a) => {
          assignationArray.push(
            new ProjectionAssignation(
              a.projectName,
              a.firstName,
              a.lastName,
              a.userExternalId,
              a.roleType,
              a.externalHours,
              a.bonusHours,
              a.shadow,
              a.externalId,
            ),
          );
        });
        projectsArray.push(new ProjectData(p.projectName, assignationArray));
      });

      projection.externalId = response.externalId;
      projection.people = peopleArray;
      projection.projects = projectsArray;
    });
    return projection;
  }

  getAssignationProjection(year: number, month: number, bonused: boolean): Promise<ApiResponseV2<ReportDTO<AssignationProjectionReport>>> {
    return HttpClientV2.get(`${this.basePath}/projections?year=${year}&month=${month}&bonused=${bonused}`);
  }

  getAssignationDetails(userExternalId: string): Promise<ApiResponseV2<OverStaffAssignation[]>> {
    return HttpClientV2.get(`${this.basePath}/details/${userExternalId}`);
  }

  getByExternalId(externalId: string): Promise<ApiResponseV2<Assignation>> {
    return HttpClientV2.get(`${this.basePath}/${externalId}`);
  }

  getOverStaffProjection(): Promise<ApiResponseV2<Projection>> {
    return HttpClientV2.get(`${this.basePath}/over-staff-projection`);
  }

  getAssignationTransferBuilder(
    positionExternalId: string,
    assignationExternalId: string,
  ): Promise<ApiResponseV2<AssignationTransferBuilder>> {
    return HttpClientV2.get(`${this.basePath}/switch-assignation-builder/${positionExternalId}/${assignationExternalId}`);
  }

  transferAssignation(assignationForm: AssignationTransferForm): Promise<ApiResponseV2<AssignationTransfer>> {
    const formData = this.parseAssignationTransferForm(assignationForm);
    return HttpClientV2.post(`${this.basePath}/switch-assignation`, formData);
  }

  validateAssignationStartDate(
    positionExternalId: string,
    assignationStartDate: string,
    assignationEndDate: string,
  ): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.get(
      `${this.basePath}/validate-position-date?positionExternalId=${positionExternalId}&` +
        `assignationStartDate=${assignationStartDate}&assignationEndDate=${assignationEndDate}`,
    );
  }
}

export default new AssignationService("/ensolvers-ops/assignation");
